import { useRef } from 'react';
import { resolveLocalizedName } from 'pi-kiosk-shared';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../../../app/pwa/customerFormDirtyGate.js';
import { toCustomerFacingErrorMessage } from '../../../../shared/errors/toCustomerFacingErrorMessage.js';
import { Card } from '../../../../shared/ui/Card.js';
import { Button } from '../../../../shared/ui/Button.js';
import { FormField } from '../../../../shared/ui/FormField.js';
import { focusFirstInvalid } from '../../../../shared/ui/focusFirstInvalid.js';
import { formatMinorAmount } from '../../hooks/usePhoneFirstDonationJourney.js';
import type { UsePhoneFirstDonationJourneyResult } from '../../hooks/usePhoneFirstDonationJourney.js';

export type PhoneFirstDonationAmountStepViewProps = Pick<
  UsePhoneFirstDonationJourneyResult,
  | 't'
  | 'hasLegacyCode'
  | 'legacyProject'
  | 'amountPresets'
  | 'amountMinor'
  | 'amountOverride'
  | 'setAmountOverride'
  | 'numberLocale'
  | 'errorMessage'
  | 'submitAmount'
>;

export function PhoneFirstDonationAmountStepView(
  props: PhoneFirstDonationAmountStepViewProps,
): JSX.Element {
  const {
    t,
    hasLegacyCode,
    amountPresets,
    amountMinor,
    amountOverride,
    setAmountOverride,
    numberLocale,
    errorMessage,
    submitAmount,
  } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const isDraftDirty = amountOverride !== null;
  const safeErrorText =
    errorMessage != null
      ? toCustomerFacingErrorMessage(errorMessage, {
          fallback: t('donation.paymentStartFailed'),
        })
      : undefined;

  return (
    <Card padded>
      <div
        className="flex flex-col gap-3"
        ref={rootRef}
        {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
      >
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('donation.amountIntro', {
            legacySuffix: hasLegacyCode ? '' : t('donation.legacySuffix'),
          })}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {amountPresets.map((presetMinor) => (
            <Button
              key={`donation-amount-${presetMinor}`}
              intent={presetMinor === amountMinor ? 'primary' : 'secondary'}
              size="md"
              block
              onClick={() => {
                setAmountOverride(presetMinor);
              }}
            >
              {formatMinorAmount(presetMinor, 'CZK', numberLocale)}
            </Button>
          ))}
        </div>
        <FormField
          id="donation-custom-amount"
          label={t('donation.customAmount')}
          type="number"
          inputMode="decimal"
          min={1}
          step={1}
          value={(amountMinor / 100).toString()}
          onChange={(e) => {
            const next = Math.round(Number(e.target.value) * 100);
            setAmountOverride(Number.isFinite(next) ? next : 0);
          }}
          errorText={safeErrorText}
          // G12 (b) — single-field amount: errorText-alone (no FormErrorSummary).
          required
        />
        {/* Continue is sync local validation + step advance — no async pending from parent. */}
        <Button
          intent="primary"
          size="lg"
          block
          onClick={() => {
            const ok = submitAmount();
            if (!ok) {
              queueMicrotask(() => {
                focusFirstInvalid(rootRef.current, ['donation-custom-amount'], {
                  knownInvalidIds: ['donation-custom-amount'],
                });
              });
            }
          }}
        >
          {t('donation.continue')}
        </Button>
      </div>
    </Card>
  );
}

export function resolveDonationAmountTitle(
  props: Pick<PhoneFirstDonationAmountStepViewProps, 't' | 'hasLegacyCode' | 'legacyProject'> & {
    readonly language?: string;
  },
): string {
  const { t, hasLegacyCode, legacyProject, language = 'en' } = props;
  if (hasLegacyCode && legacyProject.data) {
    return resolveLocalizedName(
      legacyProject.data.name,
      legacyProject.data.nameLocales,
      language,
    );
  }
  return t('donation.supportProject');
}
